import React, { useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import SearchList, { SearchListWrapper } from '../components/SearchList'
import LabelOnly from '../components/LabelOnly'
import InputOnly from '../components/InputOnly'
import ColumnRadioButton from '../components/ColumnRadioButton'
import { TitleButton } from '../components/TitleButton'
import { AddBtn } from '../components/TableAddRowButton'
import {
  findAllESSGQL,
  findOneESSGQL,
  createESSGQL,
  updateESSGQL,
  deleteESSGQL,
} from '../gql/ESS.gql'
import {
  Body,
  BodyWrapper,
  ContentWrapper,
  Header,
  InputItem,
  InputLabel,
  Left,
  Right,
  TradingMethod,
  TradingMethodBody,
} from '../style/CustomerPV.styled'
import {
  TopSection,
  UnitText,
  ListHeader,
  ListItemRow,
  DeleteBtn,
  StyledSelect,
  OpexModeControl,
  SectionHeader,
  AddButtonWrapper,
  SummaryCard,
  SummaryCardBody,
  SummaryCardKoreanAmount,
  SummaryCardLabel,
  SummaryCardUnit,
  SummaryCardValue,
} from './CustomerESS.styled'

// --- Types ---
interface OptionItem {
  optionId: string
  name: string
  unit: string
  value: number
  order: number
}
type OpexInputMode = 'REGISTRATION' | 'ANALYSIS'

const opexInputModeOptions = [
  { label: 'ESS 등록 시 입력' },
  { label: '경제성 분석 시 수익 비율로 입력' },
]

const opexInputModeToRadioValue = (inputMode?: OpexInputMode) =>
  inputMode === 'ANALYSIS' ? '1' : '0'

const radioValueToOpexInputMode = (value: string): OpexInputMode =>
  value === '1' ? 'ANALYSIS' : 'REGISTRATION'

interface ESSData {
  _id?: string
  ESS_name: string
  batteryCapKWh: number
  pcsCapKW: number
  analysisPeriodYears: number
  capex: {
    total: number
    option: OptionItem[]
  }
  opex: {
    inputMode: OpexInputMode
    total: number | null
    option: OptionItem[]
  }
}

// --- Utils ---

// 유틸: 랜덤 ID 생성
const generateId = () => Math.random().toString(36).substr(2, 9)

const formatKoreanManwon = (value?: number | null, suffix = '') => {
  const amount = Math.round(Number(value) || 0)
  const sign = amount < 0 ? '-' : ''
  const absAmount = Math.abs(amount)
  const eok = Math.floor(absAmount / 10000)
  const remainder = absAmount % 10000
  const chunManwon = Math.floor(remainder / 1000)
  const manwon = remainder % 1000
  const parts: string[] = []

  if (eok > 0) parts.push(`${eok.toLocaleString()}억`)
  if (chunManwon > 0) parts.push(`${chunManwon}천만원`)
  if (manwon > 0 || parts.length === 0) {
    parts.push(`${manwon.toLocaleString()}만원`)
  }

  return `${sign}${parts.join(' ')}${suffix}`
}

const formatEssCapacity = (ess: Partial<ESSData>) => {
  const batteryCap = Number(ess.batteryCapKWh) || 0
  const pcsCap = Number(ess.pcsCapKW) || 0

  return `${pcsCap.toLocaleString()}kW / ${batteryCap.toLocaleString()}kWh`
}

// 유틸: __typename 제거 (재귀적)
const stripTypename = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(stripTypename)
  } else if (obj !== null && typeof obj === 'object') {
    const newObj: any = {}
    for (const key in obj) {
      if (key !== '__typename') {
        newObj[key] = stripTypename(obj[key])
      }
    }
    return newObj
  }
  return obj
}

// [수정] 초기 데이터 생성 함수 (함수로 만들어야 ID가 매번 새로 생성됨)
const getInitialData = (): ESSData => {
  return {
    ESS_name: '',
    batteryCapKWh: 0,
    pcsCapKW: 0,
    analysisPeriodYears: 0,
    capex: {
      total: 0,
      // 기본으로 빈 옵션 1개 추가
      option: [
        {
          optionId: generateId(),
          name: '',
          unit: 'KRW',
          value: 0,
          order: 0,
        },
      ],
    },
    opex: {
      inputMode: 'REGISTRATION',
      total: 0,
      // 기본으로 빈 옵션 1개 추가
      option: [
        {
          optionId: generateId(),
          name: '',
          unit: 'KRW_PER_YEAR',
          value: 0,
          order: 0,
        },
      ],
    },
  }
}

// [수정] 데이터 불러오기 시 단위 변환 (원 -> 만원)
const normalizeData = (backendData: any): ESSData => {
  const init = getInitialData()

  if (!backendData) return init

  return {
    ...init,
    ...backendData,
    batteryCapKWh:
      typeof backendData.batteryCapKWh !== 'undefined'
        ? backendData.batteryCapKWh
        : (backendData.batteryCapMWh || 0) * 1000,
    pcsCapKW:
      typeof backendData.pcsCapKW !== 'undefined'
        ? backendData.pcsCapKW
        : (backendData.pcsCapMW || 0) * 1000,
    capex: backendData.capex
      ? {
          // Total: 원 -> 만원
          total: (backendData.capex.total || 0) / 10000,
          option: (backendData.capex.option || []).map((opt: any) => ({
            ...opt,
            // Option Value: 원 -> 만원 (모든 CAPEX 단위는 화폐성이라 가정)
            value: (opt.value || 0) / 10000,
          })),
        }
      : init.capex,
    opex: backendData.opex
      ? {
          inputMode: backendData.opex.inputMode || 'REGISTRATION',
          // Total: 원 -> 만원
          total:
            backendData.opex.inputMode === 'ANALYSIS'
              ? null
              : (backendData.opex.total || 0) / 10000,
          option: (backendData.opex.option || []).map((opt: any) => ({
            ...opt,
            value:
              opt.unit === 'AS_CAPEX_PERCENT'
                ? opt.value || 0
                : (opt.value || 0) / 10000,
          })),
        }
      : init.opex,
  }
}

// --- Main Component ---

const CustomerESS = () => {
  const [data, setData] = useState<ESSData>(getInitialData())
  const [listRefetch, setListRefetch] = useState<() => void>(() => () => {})

  // --- GraphQL Hooks ---
  const [getOneESS, { data: queryData }] = useLazyQuery(findOneESSGQL, {
    fetchPolicy: 'network-only',
  })

  const [createESS] = useMutation(createESSGQL)
  const [updateESS] = useMutation(updateESSGQL)
  const [deleteESS] = useMutation(deleteESSGQL)

  // --- Effects ---

  // 1. 상세 조회 데이터 반영
  useEffect(() => {
    if (queryData && queryData.findOneESS) {
      setData(normalizeData(queryData.findOneESS))
    }
  }, [queryData])

  // 2. CAPEX Total 자동 계산 (화면상 데이터는 만원 단위이므로 결과도 만원 단위)
  useEffect(() => {
    const options = data.capex?.option || []

    const sum = options.reduce((acc, curr) => {
      const val = Number(curr.value) || 0

      // 단위별 계산 로직 분기
      if (curr.unit === 'KRW_PER_MW') {
        return acc + val * (data.pcsCapKW / 1000)
      } else if (curr.unit === 'KRW_PER_KW') {
        return acc + val * data.pcsCapKW
      } else if (curr.unit === 'KRW_PER_MWH') {
        return acc + val * (data.batteryCapKWh / 1000)
      } else if (curr.unit === 'KRW_PER_KWH') {
        return acc + val * data.batteryCapKWh
      } else {
        // 'KRW' 및 기타
        return acc + val
      }
    }, 0)

    // 계산된 값이 기존과 다를 때만 업데이트
    if (data.capex?.total !== sum) {
      setData((prev) => ({
        ...prev,
        capex: { ...prev.capex, total: sum },
      }))
    }
  }, [
    data.capex?.option,
    data.pcsCapKW,
    data.batteryCapKWh,
    // data.capex.total 의존성 제외 (무한루프 방지, 값 비교로 처리)
  ])

  // 3. OPEX Total 자동 계산
  useEffect(() => {
    const opex = data.opex || {
      option: [],
      total: 0,
    }
    if (opex.inputMode === 'ANALYSIS') {
      if (opex.total !== null || (opex.option || []).length !== 0) {
        setData((prev) => ({
          ...prev,
          opex: { ...prev.opex, total: null, option: [] },
        }))
      }
      return
    }

    const capexTotal = data.capex?.total || 0
    const sum = (opex.option || []).reduce((acc, curr) => {
      const value = Number(curr.value) || 0
      if (curr.unit === 'AS_CAPEX_PERCENT') {
        return acc + capexTotal * (value / 100)
      }
      if (curr.unit === 'KRW_PER_MW_YEAR') {
        return acc + value * (data.pcsCapKW / 1000)
      }
      if (curr.unit === 'KRW_PER_KW_YEAR') {
        return acc + value * data.pcsCapKW
      }
      if (curr.unit === 'KRW_PER_MWH_YEAR') {
        return acc + value * (data.batteryCapKWh / 1000)
      }
      if (curr.unit === 'KRW_PER_KWH_YEAR') {
        return acc + value * data.batteryCapKWh
      }
      return acc + value
    }, 0)

    // 최적화: 계산된 합계가 기존과 다를 때만 업데이트
    if (opex.total !== sum) {
      setData((prev) => ({
        ...prev,
        opex: { ...prev.opex, total: sum },
      }))
    }
  }, [
    data.opex?.option,
    data.opex?.inputMode,
    data.capex?.total,
    data.pcsCapKW,
    data.batteryCapKWh,
    data.opex?.total, // 비교를 위해 의존성 추가
  ])

  // --- Handlers ---

  const handleFindOne = (id: string) => {
    getOneESS({ variables: { val: id } })
  }

  const handleResetAll = () => {
    setData(getInitialData())
  }

  const onSave = async () => {
    try {
      // 1. 깊은 복사 & __typename 제거
      // (기존 로직 그대로 유지)
      const cleanData = JSON.parse(JSON.stringify(stripTypename(data)))

      // 2. [저장 전 단위 변환] 만원 -> 원 (x10000)
      // (기존 변환 로직 그대로 유지)

      // (1) CAPEX 변환
      if (cleanData.capex) {
        cleanData.capex.total = (cleanData.capex.total || 0) * 10000
        if (Array.isArray(cleanData.capex.option)) {
          cleanData.capex.option = cleanData.capex.option.map((opt: any) => ({
            ...opt,
            value: (opt.value || 0) * 10000,
          }))
        }
      }

      // (2) OPEX 변환
      if (cleanData.opex) {
        cleanData.opex.total =
          cleanData.opex.inputMode === 'ANALYSIS'
            ? null
            : (cleanData.opex.total || 0) * 10000
        if (Array.isArray(cleanData.opex.option)) {
          cleanData.opex.option = cleanData.opex.option.map((opt: any) => ({
            ...opt,
            value:
              opt.unit === 'AS_CAPEX_PERCENT'
                ? opt.value || 0
                : (opt.value || 0) * 10000,
          }))
        }
        if (cleanData.opex.inputMode === 'ANALYSIS') {
          cleanData.opex.option = []
        }
      }

      // 3. 시스템 필드 분리
      const { createdAt, updatedAt, deletedAt, _id, ...inputData } = cleanData

      // 4. DB 요청 (await 필수!)
      if (_id) {
        // [UPDATE]
        await updateESS({
          variables: {
            val: { _id, ...inputData },
          },
        })
        // [수정] alert 제거 -> 팝업이 성공 화면을 보여줌
      } else {
        // [CREATE]
        const { data: res } = await createESS({
          variables: {
            val: inputData,
          },
        })

        // 생성된 ID 반영
        if (res && res.createESS && res.createESS._id) {
          setData((prev) => ({
            ...prev,
            _id: res.createESS._id,
          }))
        }
        // [수정] alert 제거
      }

      // 목록 갱신
      if (listRefetch) listRefetch()
    } catch (e) {
      console.error('Save Error:', e)
      // [중요] 에러가 발생했음을 팝업에게 알려주기 위해 에러를 다시 던집니다.
      // 이걸 안 하면 팝업은 성공한 줄 알고 "저장 완료"를 띄웁니다.
      alert('저장 중 오류가 발생했습니다.') // 실패 알림은 남겨도 됨
      throw e
    }
  }

  const onDelete = async () => {
    if (!data._id) {
      alert('삭제할 데이터가 선택되지 않았습니다.')
      return
    }

    try {
      await deleteESS({ variables: { val: data._id } })

      // 목록 갱신 및 초기화
      if (listRefetch) listRefetch()
      handleResetAll()
    } catch (e) {
      console.error(e)
      alert('삭제 중 오류가 발생했습니다.')
      throw e
    }
  }

  // --- Form Input Handlers ---

  const handleBasicChange = (field: keyof ESSData, value: any) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  // --- CAPEX Option Handlers ---
  const handleCapexOptionChange = (
    id: string,
    field: keyof OptionItem,
    value: any
  ) => {
    const currentOptions = data.capex?.option || []
    const newOptions = currentOptions.map((opt) =>
      opt.optionId === id ? { ...opt, [field]: value } : opt
    )
    setData((prev) => ({
      ...prev,
      capex: { ...prev.capex, option: newOptions },
    }))
  }

  const addCapexOption = () => {
    const newOption: OptionItem = {
      optionId: generateId(),
      name: '',
      unit: 'KRW',
      value: 0,
      order: (data.capex?.option || []).length,
    }
    setData((prev) => ({
      ...prev,
      capex: {
        ...prev.capex,
        option: [...(prev.capex?.option || []), newOption],
      },
    }))
  }

  const removeCapexOption = (id: string) => {
    const currentOptions = data.capex?.option || []
    const newOptions = currentOptions.filter((opt) => opt.optionId !== id)
    setData((prev) => ({
      ...prev,
      capex: { ...prev.capex, option: newOptions },
    }))
  }

  // --- OPEX Option Handlers ---
  const handleOpexOptionChange = (
    id: string,
    field: keyof OptionItem,
    value: any
  ) => {
    const currentOptions = data.opex?.option || []
    const newOptions = currentOptions.map((opt) =>
      opt.optionId === id ? { ...opt, [field]: value } : opt
    )
    setData((prev) => ({
      ...prev,
      opex: { ...prev.opex, option: newOptions },
    }))
  }

  const handleOpexInputModeChange = (inputMode: OpexInputMode) => {
    setData((prev) => ({
      ...prev,
      opex:
        inputMode === 'REGISTRATION'
          ? {
              ...prev.opex,
              inputMode,
              option:
                prev.opex?.option?.length > 0
                  ? prev.opex.option
                  : [
                      {
                        optionId: generateId(),
                        name: '',
                        unit: 'KRW_PER_YEAR',
                        value: 0,
                        order: 0,
                      },
                    ],
            }
          : {
              ...prev.opex,
              inputMode,
              total: null,
              option: [],
            },
    }))
  }

  const addOpexOption = () => {
    const newOption: OptionItem = {
      optionId: generateId(),
      name: '',
      unit: 'KRW_PER_YEAR',
      value: 0,
      order: (data.opex?.option || []).length,
    }
    setData((prev) => ({
      ...prev,
      opex: {
        ...prev.opex,
        option: [...(prev.opex?.option || []), newOption],
      },
    }))
  }

  const removeOpexOption = (id: string) => {
    const currentOptions = data.opex?.option || []
    const newOptions = currentOptions.filter((opt) => opt.optionId !== id)
    setData((prev) => ({
      ...prev,
      opex: { ...prev.opex, option: newOptions },
    }))
  }

  return (
    <>
      <SearchListWrapper>
        <SearchList
          title="ESS 프로젝트 목록"
          findAllListgql={findAllESSGQL}
          findOne={getOneESS}
          onReset={handleResetAll}
          setRefetch={setListRefetch}
          nameKey="ESS_name"
          descriptionRenderer={formatEssCapacity}
        />
      </SearchListWrapper>
      <ContentWrapper>
        <Header>
          <TitleButton
            title="ESS 설비·비용 정보"
            onSaveClick={onSave}
            onCancleClick={handleResetAll}
            onDeleteClick={onDelete}
            isModify={!!data._id}
          />
        </Header>

        <BodyWrapper>
          <TopSection>
            <InputItem style={{ flex: 1 }}>
              <InputLabel>ESS 프로젝트명</InputLabel>
              <div style={{ display: 'flex' }}>
                <InputOnly
                  value={data.ESS_name}
                  onChange={(e) =>
                    handleBasicChange('ESS_name', e.target.value)
                  }
                  placeholder="이름 입력"
                />
              </div>
            </InputItem>
            <InputItem style={{ flex: 1 }}>
              <InputLabel>PCS 용량</InputLabel>
              <div style={{ display: 'flex' }}>
                <InputOnly
                  type="number"
                  value={data.pcsCapKW}
                  onChange={(e) =>
                    handleBasicChange('pcsCapKW', Number(e.target.value))
                  }
                  placeholder="0"
                />
                <UnitText>kW</UnitText>
              </div>
            </InputItem>
            <InputItem style={{ flex: 1 }}>
              <InputLabel>배터리 용량</InputLabel>
              <div style={{ display: 'flex' }}>
                <InputOnly
                  type="number"
                  value={data.batteryCapKWh}
                  onChange={(e) =>
                    handleBasicChange('batteryCapKWh', Number(e.target.value))
                  }
                  placeholder="0"
                />
                <UnitText>kWh</UnitText>
              </div>
            </InputItem>
            <InputItem style={{ flex: 1 }}>
              <InputLabel>사업 기간</InputLabel>
              <div style={{ display: 'flex' }}>
                <InputOnly
                  type="number"
                  value={data.analysisPeriodYears}
                  onChange={(e) =>
                    handleBasicChange(
                      'analysisPeriodYears',
                      Number(e.target.value)
                    )
                  }
                  placeholder="0"
                />
                <UnitText>년</UnitText>
              </div>
            </InputItem>
          </TopSection>

          <Body>
            {/* 좌측: CAPEX */}
            <Left style={{ flex: '1 1 0', width: 0 }}>
              <SectionHeader>
                <LabelOnly label="CAPEX" />
              </SectionHeader>
              <SummaryCard>
                <SummaryCardLabel>합계</SummaryCardLabel>
                <SummaryCardBody>
                  <SummaryCardValue>
                    {data.capex?.total.toLocaleString() || '0'}
                  </SummaryCardValue>
                  <SummaryCardUnit>만원</SummaryCardUnit>
                  <SummaryCardKoreanAmount>
                    {formatKoreanManwon(data.capex?.total)}
                  </SummaryCardKoreanAmount>
                </SummaryCardBody>
              </SummaryCard>
              <TradingMethod>
                <TradingMethodBody>
                  <ListHeader>
                    <div>항목명</div>
                    <div>단가</div>
                    <div>단위</div>
                    <div style={{ textAlign: 'center' }}>삭제</div>
                  </ListHeader>
                  {(data.capex?.option || []).map((opt) => (
                    <ListItemRow key={opt.optionId}>
                      <InputOnly
                        value={opt.name}
                        onChange={(e) =>
                          handleCapexOptionChange(
                            opt.optionId,
                            'name',
                            e.target.value
                          )
                        }
                        placeholder="항목명"
                      />
                      <InputOnly
                        type="number"
                        value={opt.value}
                        onChange={(e) =>
                          handleCapexOptionChange(
                            opt.optionId,
                            'value',
                            Number(e.target.value)
                          )
                        }
                        placeholder="금액"
                      />
                      <StyledSelect
                        value={opt.unit}
                        onChange={(e) =>
                          handleCapexOptionChange(
                            opt.optionId,
                            'unit',
                            e.target.value
                          )
                        }
                      >
                        <option value={'KRW'}>만원</option>
                        <option value={'KRW_PER_MW'}>만원/MW</option>
                        <option value={'KRW_PER_KW'}>만원/kW</option>
                        <option value={'KRW_PER_MWH'}>만원/MWh</option>
                        <option value={'KRW_PER_KWH'}>만원/kWh</option>
                      </StyledSelect>
                      <DeleteBtn
                        onClick={() => removeCapexOption(opt.optionId)}
                      >
                        삭제
                      </DeleteBtn>
                    </ListItemRow>
                  ))}
                </TradingMethodBody>
              </TradingMethod>
              <AddButtonWrapper>
                <AddBtn onAdd={addCapexOption} size="lg" />
              </AddButtonWrapper>
            </Left>

            <Right style={{ flex: '1 1 0', width: 0, gap: '32px' }}>
              <SectionHeader>
                <LabelOnly label="OPEX" />
                <OpexModeControl>
                  <ColumnRadioButton
                    row
                    list={opexInputModeOptions}
                    value={opexInputModeToRadioValue(data.opex?.inputMode)}
                    onChange={(value) =>
                      handleOpexInputModeChange(
                        radioValueToOpexInputMode(value)
                      )
                    }
                  />
                </OpexModeControl>
              </SectionHeader>
              {(data.opex?.inputMode || 'REGISTRATION') === 'REGISTRATION' && (
                <>
                  <SummaryCard>
                    <SummaryCardLabel>합계</SummaryCardLabel>
                    <SummaryCardBody>
                      <SummaryCardValue>
                        {data.opex?.total?.toLocaleString() || '0'}
                      </SummaryCardValue>
                      <SummaryCardUnit>만원/year</SummaryCardUnit>
                      <SummaryCardKoreanAmount>
                        {formatKoreanManwon(data.opex?.total, '/year')}
                      </SummaryCardKoreanAmount>
                    </SummaryCardBody>
                  </SummaryCard>
                  <TradingMethod>
                    <TradingMethodBody>
                      <ListHeader>
                        <div>항목명</div>
                        <div>단가</div>
                        <div>단위</div>
                        <div style={{ textAlign: 'center' }}>삭제</div>
                      </ListHeader>

                      {(data.opex?.option || []).map((opt) => (
                        <ListItemRow key={opt.optionId}>
                          <InputOnly
                            value={opt.name}
                            onChange={(e) =>
                              handleOpexOptionChange(
                                opt.optionId,
                                'name',
                                e.target.value
                              )
                            }
                            placeholder="항목명"
                          />
                          <InputOnly
                            type="number"
                            value={opt.value}
                            onChange={(e) =>
                              handleOpexOptionChange(
                                opt.optionId,
                                'value',
                                Number(e.target.value)
                              )
                            }
                            placeholder="금액"
                          />
                          <StyledSelect
                            value={opt.unit}
                            onChange={(e) =>
                              handleOpexOptionChange(
                                opt.optionId,
                                'unit',
                                e.target.value
                              )
                            }
                          >
                            <option value={'KRW_PER_YEAR'}>만원/year</option>
                            <option value={'KRW_PER_MW_YEAR'}>
                              만원/MW·year
                            </option>
                            <option value={'KRW_PER_MWH_YEAR'}>
                              만원/MWh·year
                            </option>
                            <option value={'KRW_PER_KW_YEAR'}>
                              만원/kW·year
                            </option>
                            <option value={'KRW_PER_KWH_YEAR'}>
                              만원/kWh·year
                            </option>
                            <option value={'AS_CAPEX_PERCENT'}>
                              CAPEX 대비 %/year
                            </option>
                          </StyledSelect>
                          <DeleteBtn
                            onClick={() => removeOpexOption(opt.optionId)}
                          >
                            삭제
                          </DeleteBtn>
                        </ListItemRow>
                      ))}
                    </TradingMethodBody>
                  </TradingMethod>
                  <AddButtonWrapper>
                    <AddBtn onAdd={addOpexOption} size="lg" />
                  </AddButtonWrapper>
                </>
              )}
            </Right>
          </Body>
        </BodyWrapper>
      </ContentWrapper>
    </>
  )
}

export default CustomerESS
